import * as React from "react";
import Iconify from "./iconify";
import { IconButtonAnimate } from "./animate";
import PropTypes from "prop-types";
import { Button, Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { getShortDayNameFromDate } from "src/utils/formatTime";
import { useLocales } from "src/locales";

const SquareButton = styled(Button)(({ theme }) => ({
  width: 40,
  height: 40,
  minWidth: 0,
}));

ButtonArrayDatePicker.propTypes = {
  date: PropTypes.instanceOf(Date),
  changeDate: PropTypes.func,
  isSmall: PropTypes.bool,
  isPermanentEvent: PropTypes.bool,
};

const areDatesOnSameDay = (date1, date2) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export default function ButtonArrayDatePicker({
  date,
  changeDate,
  isSmall = false,
  isLoading,
  isPermanentEvent = true,
}) {
  const {
    currentLang: { value },
  } = useLocales();

  const getDateButtons = () => {
    const indices = isSmall
      ? Array.from({ length: 3 }, (_, index) => index - 1)
      : Array.from({ length: 5 }, (_, index) => index - 2);

    const datesArray = indices.map((offset) => {
      const newDate = new Date(date);
      newDate.setDate(date.getDate() + offset);
      return {
        date: newDate,
        dayName: getShortDayNameFromDate(newDate, value),
      };
    });

    return (
      <Box
        sx={{
          display: "flex",
          gap: 1,
        }}
      >
        {datesArray.map((d, index) => {
          return (
            <SquareButton
              onClick={() => {
                changeDate(d.date);
              }}
              color={areDatesOnSameDay(d.date, date) ? "success" : "inherit"}
              sx={{
                backgroundColor: (t) => {
                  return areDatesOnSameDay(d.date, date)
                    ? t.palette.common.white
                    : t.palette.background.secondary;
                },
                "&:hover": {
                  backgroundColor: (t) => {
                    return areDatesOnSameDay(d.date, date)
                      ? t.palette.common.white
                      : 'default';
                  },
                },
                color: areDatesOnSameDay(d.date, date) ? "#252525" : "white",
              }}
              key={index}
              disabled={!isPermanentEvent && !areDatesOnSameDay(d.date, date)}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="caption">{d.dayName}</Typography>
                <Typography variant="caption">{d.date.getDate()}</Typography>
              </Box>
            </SquareButton>
          );
        })}
      </Box>
    );
  };

  return (
    <React.Fragment>
      <IconButtonAnimate
        onClick={() => {
          const newDate = new Date(date);
          newDate.setDate(date.getDate() - 1);
          changeDate(newDate);
        }}
        disabled={isLoading || !isPermanentEvent}
      >
        <Iconify icon="ic:round-chevron-left" width={25} sx={{color: "white"}} />
      </IconButtonAnimate>
      {getDateButtons()}
      <IconButtonAnimate
        onClick={() => {
          const newDate = new Date(date);
          newDate.setDate(date.getDate() + 1);
          changeDate(newDate);
        }}
        disabled={isLoading || !isPermanentEvent}
      >
        <Iconify icon="ic:round-chevron-right" width={25} sx={{color: "white"}} />
      </IconButtonAnimate>
    </React.Fragment>
  );
}
